import { FESTIVALS } from "@/lib/festival-weather/festivals";
import { catalogueQuizProfile } from "./catalogue-profiles";

export type QuizAxis =
  | "intimacy"
  | "productionScale"
  | "campingComfort"
  | "musicDiscovery"
  | "crowdEnergy"
  | "artsCulture"
  | "weatherResilience"
  | "communityFeel"
  | "durationCommitment"
  | "genrePurity";

export type GenreAffinity = "electronic" | "rock" | "urban" | "folk";

export type RegionFilter = "uk" | "europe" | "north-america" | "anz" | "anywhere";

export type FestivalQuizData = {
  slug: string;
  name: string;
  region: "uk" | "europe" | "north-america" | "anz";
  axes: Record<QuizAxis, number>;
  genreTags: GenreAffinity[];
  pitchlines: Partial<Record<QuizAxis, string>>;
  gradient: string;
  emoji: string;
  locationLabel: string;
  capacity?: number;
  month?: string;
};

export type FestivalAnswer = {
  id: string;
  label: string;
  targets?: { axis: QuizAxis; ideal: number; weight?: number }[];
  genreAffinity?: GenreAffinity;
  regionFilter?: RegionFilter;
};

export type FestivalQuestion = {
  id: string;
  order: number;
  prompt: string;
  answers: FestivalAnswer[];
  doubleWeight?: boolean;
};

export const FESTIVAL_QUESTIONS: FestivalQuestion[] = [
  {
    id: "geo",
    order: 1,
    prompt: "Where are you looking for festivals?",
    answers: [
      { id: "uk", label: "UK", regionFilter: "uk" },
      { id: "europe", label: "Europe", regionFilter: "europe" },
      { id: "north-america", label: "North America", regionFilter: "north-america" },
      { id: "anz", label: "Australia and NZ", regionFilter: "anz" },
      { id: "anywhere", label: "Anywhere", regionFilter: "anywhere" },
    ],
  },
  {
    id: "lineup",
    order: 2,
    prompt: "What draws you to the lineup?",
    answers: [
      {
        id: "headliners",
        label: "I want my favourite headliners front and centre",
        targets: [{ axis: "musicDiscovery", ideal: 2, weight: 1.0 }],
      },
      {
        id: "discover",
        label: "I go to discover acts I\u2019ve never heard of",
        targets: [{ axis: "musicDiscovery", ideal: 9, weight: 1.0 }],
      },
      {
        id: "experience",
        label: "The lineup\u2019s a bonus, I\u2019m there for the experience",
        targets: [
          { axis: "musicDiscovery", ideal: 5, weight: 0.6 },
          { axis: "artsCulture", ideal: 7, weight: 0.8 },
        ],
      },
    ],
  },
  {
    id: "genre",
    order: 3,
    prompt: "What\u2019s playing in your headphones right now?",
    answers: [
      { id: "electronic", label: "Electronic, dance, techno", genreAffinity: "electronic" },
      { id: "rock", label: "Rock, indie, punk, metal", genreAffinity: "rock" },
      { id: "urban", label: "Hip-hop, grime, R&B", genreAffinity: "urban" },
      { id: "folk", label: "Folk, world, acoustic, singer-songwriter", genreAffinity: "folk" },
      { id: "everything", label: "A bit of everything honestly" },
    ],
  },
  {
    id: "size",
    order: 4,
    prompt: "Your ideal crowd size?",
    answers: [
      {
        id: "intimate",
        label: "Intimate. I want to recognise faces by Sunday",
        targets: [{ axis: "intimacy", ideal: 9, weight: 1.0 }],
      },
      {
        id: "medium",
        label: "Big enough to get lost in but not a city",
        targets: [{ axis: "intimacy", ideal: 5, weight: 1.0 }],
      },
      {
        id: "massive",
        label: "Absolute sensory overload, the bigger the better",
        targets: [{ axis: "intimacy", ideal: 2, weight: 1.0 }],
      },
    ],
  },
  {
    id: "friday",
    order: 5,
    prompt: "Friday night. Where are you?",
    answers: [
      {
        id: "front-row",
        label: "Front row at the headline set, losing my mind",
        targets: [{ axis: "crowdEnergy", ideal: 9, weight: 1.0 }],
      },
      {
        id: "hidden-stage",
        label: "Deep in a hidden stage no one else has found",
        targets: [
          { axis: "intimacy", ideal: 7, weight: 0.7 },
          { axis: "musicDiscovery", ideal: 8, weight: 0.8 },
        ],
      },
      {
        id: "campfire",
        label: "Around a fire with strangers who feel like friends",
        targets: [
          { axis: "communityFeel", ideal: 9, weight: 1.0 },
          { axis: "crowdEnergy", ideal: 3, weight: 0.5 },
        ],
      },
      {
        id: "art",
        label: "At the art installation or comedy tent",
        targets: [{ axis: "artsCulture", ideal: 9, weight: 1.0 }],
      },
    ],
  },
  {
    id: "camping",
    order: 6,
    prompt: "What\u2019s your camping philosophy?",
    answers: [
      {
        id: "basic",
        label: "A tent, a sleeping bag, whatever happens happens",
        targets: [{ axis: "campingComfort", ideal: 2, weight: 1.0 }],
      },
      {
        id: "upgrade",
        label: "I\u2019ll upgrade to a bell tent or pre-pitched setup",
        targets: [{ axis: "campingComfort", ideal: 6, weight: 1.0 }],
      },
      {
        id: "glamping",
        label: "I want a real bed. Glamping or nearby accommodation",
        targets: [{ axis: "campingComfort", ideal: 9, weight: 1.0 }],
      },
      {
        id: "mud",
        label: "Mud is half the fun",
        targets: [
          { axis: "campingComfort", ideal: 1, weight: 0.7 },
          { axis: "weatherResilience", ideal: 9, weight: 1.0 },
        ],
      },
    ],
  },
  {
    id: "weather",
    order: 7,
    prompt: "The forecast says rain all weekend. You\u2026",
    answers: [
      {
        id: "cancel",
        label: "Cancel. Life\u2019s too short",
        targets: [{ axis: "weatherResilience", ideal: 1, weight: 1.0 }],
      },
      {
        id: "embrace",
        label: "Pack wellies and embrace it",
        targets: [{ axis: "weatherResilience", ideal: 7, weight: 1.0 }],
      },
      {
        id: "love-it",
        label: "This is what I signed up for",
        targets: [{ axis: "weatherResilience", ideal: 10, weight: 1.0 }],
      },
    ],
  },
  {
    id: "stage",
    order: 8,
    prompt: "Which stage are you spending most of your time at?",
    answers: [
      {
        id: "main",
        label: "The main stage. I came for the production",
        targets: [{ axis: "productionScale", ideal: 9, weight: 1.0 }],
      },
      {
        id: "tiny",
        label: "A tiny tent with a DJ and 50 people",
        targets: [
          { axis: "productionScale", ideal: 2, weight: 0.8 },
          { axis: "intimacy", ideal: 8, weight: 0.7 },
        ],
      },
      {
        id: "wander",
        label: "Wherever the energy takes me, I don\u2019t plan",
        targets: [
          { axis: "genrePurity", ideal: 2, weight: 0.7 },
          { axis: "crowdEnergy", ideal: 6, weight: 0.6 },
        ],
      },
      {
        id: "lasers",
        label: "The one with lasers, pyro, and a rig that rattles your chest",
        targets: [
          { axis: "productionScale", ideal: 10, weight: 1.0 },
          { axis: "crowdEnergy", ideal: 8, weight: 0.6 },
        ],
      },
    ],
  },
  {
    id: "arts",
    order: 9,
    prompt: "How important is the non-music stuff?",
    answers: [
      {
        id: "essential",
        label: "Essential. I\u2019m there for art, theatre, workshops, talks",
        targets: [{ axis: "artsCulture", ideal: 10, weight: 1.0 }],
      },
      {
        id: "bonus",
        label: "Nice bonus but the music comes first",
        targets: [{ axis: "artsCulture", ideal: 4, weight: 1.0 }],
      },
      {
        id: "none",
        label: "I barely leave the music stages",
        targets: [{ axis: "artsCulture", ideal: 1, weight: 1.0 }],
      },
    ],
  },
  {
    id: "loyalty",
    order: 10,
    prompt: "Do you go back to the same festival year after year?",
    answers: [
      {
        id: "pilgrimage",
        label: "Yes. It\u2019s my annual pilgrimage",
        targets: [{ axis: "communityFeel", ideal: 9, weight: 1.0 }],
      },
      {
        id: "rotate",
        label: "I rotate. Too many to try",
        targets: [{ axis: "communityFeel", ideal: 3, weight: 1.0 }],
      },
      {
        id: "my-people",
        label: "I\u2019ve found my people at one place and I stick with them",
        targets: [
          { axis: "communityFeel", ideal: 10, weight: 1.0 },
          { axis: "intimacy", ideal: 7, weight: 0.5 },
        ],
      },
    ],
  },
  {
    id: "duration",
    order: 11,
    prompt: "Ideal festival length?",
    answers: [
      {
        id: "weekend",
        label: "Weekend warrior. Friday to Sunday",
        targets: [{ axis: "durationCommitment", ideal: 3, weight: 1.0 }],
      },
      {
        id: "midweek",
        label: "Four to five days is the sweet spot",
        targets: [{ axis: "durationCommitment", ideal: 6, weight: 1.0 }],
      },
      {
        id: "week",
        label: "A full week or more. I want to settle in",
        targets: [{ axis: "durationCommitment", ideal: 9, weight: 1.0 }],
      },
    ],
  },
  {
    id: "breadth",
    order: 12,
    prompt: "What kind of lineup do you want?",
    answers: [
      {
        id: "pure",
        label: "One genre done perfectly. Every act in my wheelhouse",
        targets: [{ axis: "genrePurity", ideal: 9, weight: 1.0 }],
      },
      {
        id: "eclectic",
        label: "Eclectic. I want hip-hop next to techno next to folk",
        targets: [{ axis: "genrePurity", ideal: 2, weight: 1.0 }],
      },
      {
        id: "mixed",
        label: "A couple of genres I love with room to explore",
        targets: [{ axis: "genrePurity", ideal: 5, weight: 1.0 }],
      },
    ],
  },
  {
    id: "comeback",
    order: 13,
    prompt: "What\u2019s the one thing that would make you come back?",
    doubleWeight: true,
    answers: [
      {
        id: "lineup-unique",
        label: "A lineup I couldn\u2019t stream anywhere else",
        targets: [{ axis: "musicDiscovery", ideal: 9, weight: 1.0 }],
      },
      {
        id: "family",
        label: "A crowd that feels like family",
        targets: [{ axis: "communityFeel", ideal: 10, weight: 1.0 }],
      },
      {
        id: "production",
        label: "Production that made my jaw drop",
        targets: [{ axis: "productionScale", ideal: 10, weight: 1.0 }],
      },
      {
        id: "freedom",
        label: "The freedom to be whoever I want for a weekend",
        targets: [
          { axis: "artsCulture", ideal: 7, weight: 0.8 },
          { axis: "crowdEnergy", ideal: 6, weight: 0.6 },
        ],
      },
      {
        id: "vibe",
        label: "A vibe that\u2019s impossible to explain to someone who wasn\u2019t there",
        targets: [
          { axis: "communityFeel", ideal: 8, weight: 0.8 },
          { axis: "intimacy", ideal: 7, weight: 0.7 },
        ],
      },
    ],
  },
];

const CURATED_FESTIVAL_POOL: FestivalQuizData[] = [
  // UK (13 festivals)
  {
    slug: "reading-leeds",
    name: "Reading & Leeds",
    region: "uk",
    locationLabel: "Reading & Leeds, UK",
    capacity: 90000,
    month: "August",
    emoji: "🎸",
    gradient: "linear-gradient(135deg, #1a1a2e 0%, #e94560 60%, #f5a623 100%)",
    genreTags: ["rock"],
    axes: {
      intimacy: 2,
      productionScale: 8,
      campingComfort: 3,
      musicDiscovery: 4,
      crowdEnergy: 9,
      artsCulture: 3,
      weatherResilience: 7,
      communityFeel: 6,
      durationCommitment: 4,
      genrePurity: 4,
    },
    pitchlines: {
      crowdEnergy: "The kind of crowd that turns the campsite into a party before the gates open",
      productionScale: "Two cities, same weekend, arena-scale stages at both",
      weatherResilience: "Bank holiday mud is half the story here",
      communityFeel: "The festival people come back to every August since they were 16",
    },
  },
  {
    slug: "boomtown",
    name: "Boomtown",
    region: "uk",
    locationLabel: "Winchester, UK",
    capacity: 76000,
    month: "August",
    emoji: "🎭",
    gradient: "linear-gradient(135deg, #1B2840 0%, #5C3A6E 60%, #E6A94A 100%)",
    genreTags: ["electronic"],
    axes: {
      intimacy: 4,
      productionScale: 9,
      campingComfort: 3,
      musicDiscovery: 8,
      crowdEnergy: 9,
      artsCulture: 10,
      weatherResilience: 7,
      communityFeel: 8,
      durationCommitment: 6,
      genrePurity: 2,
    },
    pitchlines: {
      artsCulture: "A city-sized art installation that happens to have music",
      crowdEnergy: "70,000 people who treat Thursday as the main event",
      musicDiscovery: "Twelve districts, each with a sound you\u2019ve never heard before",
      communityFeel: "The kind of place where strangers build the set together",
    },
  },
  {
    slug: "download",
    name: "Download Festival",
    region: "uk",
    locationLabel: "Donington, UK",
    capacity: 111000,
    month: "June",
    emoji: "🤘",
    gradient: "linear-gradient(135deg, #0d0d0d 0%, #3d0f0f 60%, #8b0000 100%)",
    genreTags: ["rock"],
    axes: {
      intimacy: 2,
      productionScale: 9,
      campingComfort: 3,
      musicDiscovery: 3,
      crowdEnergy: 9,
      artsCulture: 2,
      weatherResilience: 9,
      communityFeel: 9,
      durationCommitment: 6,
      genrePurity: 9,
    },
    pitchlines: {
      communityFeel: "The most loyal crowd in UK festivals. Same faces, same field, every June",
      genrePurity: "Three days of metal done perfectly. No filler genres",
      crowdEnergy: "Wall of death at 2pm, circle pit by 3, and that\u2019s just the warm-up",
      weatherResilience: "Donington mud is a badge of honour here",
    },
  },
  {
    slug: "parklife",
    name: "Parklife",
    region: "uk",
    locationLabel: "Manchester, UK",
    capacity: 80000,
    month: "June",
    emoji: "🕺",
    gradient: "linear-gradient(135deg, #1a0533 0%, #6b21a8 50%, #f97316 100%)",
    genreTags: ["electronic", "urban"],
    axes: {
      intimacy: 3,
      productionScale: 8,
      campingComfort: 8,
      musicDiscovery: 5,
      crowdEnergy: 9,
      artsCulture: 3,
      weatherResilience: 4,
      communityFeel: 5,
      durationCommitment: 2,
      genrePurity: 4,
    },
    pitchlines: {
      crowdEnergy: "Manchester\u2019s biggest weekend. The crowd brings the energy the city\u2019s famous for",
      campingComfort: "No camping. Home, hotel, or Uber. Wake up clean",
      productionScale: "Arena-level production dropped into Heaton Park for two days",
    },
  },
  {
    slug: "boardmasters",
    name: "Boardmasters",
    region: "uk",
    locationLabel: "Newquay, UK",
    capacity: 53000,
    month: "August",
    emoji: "🏄",
    gradient: "linear-gradient(135deg, #0c4a6e 0%, #22d3ee 50%, #fbbf24 100%)",
    genreTags: ["electronic", "rock"],
    axes: {
      intimacy: 5,
      productionScale: 6,
      campingComfort: 4,
      musicDiscovery: 5,
      crowdEnergy: 7,
      artsCulture: 4,
      weatherResilience: 6,
      communityFeel: 7,
      durationCommitment: 6,
      genrePurity: 3,
    },
    pitchlines: {
      communityFeel: "Surf by day, music by night. The crew that comes back every August",
      crowdEnergy: "Watergate Bay energy with Atlantic winds and sunset sets",
      campingComfort: "Clifftop camping with the ocean as your morning alarm",
    },
  },
  {
    slug: "latitude",
    name: "Latitude",
    region: "uk",
    locationLabel: "Suffolk, UK",
    capacity: 40000,
    month: "July",
    emoji: "🦩",
    gradient: "linear-gradient(135deg, #134e5e 0%, #71b280 50%, #fceabb 100%)",
    genreTags: ["folk", "rock"],
    axes: {
      intimacy: 7,
      productionScale: 5,
      campingComfort: 7,
      musicDiscovery: 7,
      crowdEnergy: 4,
      artsCulture: 9,
      weatherResilience: 4,
      communityFeel: 7,
      durationCommitment: 5,
      genrePurity: 3,
    },
    pitchlines: {
      artsCulture: "The poetry tent is as packed as the main stage here",
      intimacy: "Small enough that you bump into the same people all weekend",
      musicDiscovery: "The lineup rewards curiosity. Wander and you\u2019ll find something",
      communityFeel: "The family-friendly one where everyone actually means it",
    },
  },
  {
    slug: "creamfields",
    name: "Creamfields",
    region: "uk",
    locationLabel: "Daresbury, UK",
    capacity: 70000,
    month: "August",
    emoji: "🎧",
    gradient: "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
    genreTags: ["electronic"],
    axes: {
      intimacy: 3,
      productionScale: 10,
      campingComfort: 5,
      musicDiscovery: 4,
      crowdEnergy: 9,
      artsCulture: 1,
      weatherResilience: 6,
      communityFeel: 5,
      durationCommitment: 5,
      genrePurity: 9,
    },
    pitchlines: {
      productionScale: "The stage rigs here are built for one thing: bass you feel in your chest",
      genrePurity: "Electronic music and nothing else. No compromise",
      crowdEnergy: "Bank holiday weekend energy channelled through world-class sound systems",
    },
  },
  {
    slug: "glastonbury",
    name: "Glastonbury",
    region: "uk",
    locationLabel: "Pilton, Somerset, UK",
    capacity: 210000,
    month: "June",
    emoji: "🐄",
    gradient: "linear-gradient(135deg, #2d5016 0%, #8b6914 40%, #f5deb3 100%)",
    genreTags: ["rock", "folk"],
    axes: {
      intimacy: 1,
      productionScale: 9,
      campingComfort: 4,
      musicDiscovery: 9,
      crowdEnergy: 8,
      artsCulture: 10,
      weatherResilience: 9,
      communityFeel: 9,
      durationCommitment: 7,
      genrePurity: 1,
    },
    pitchlines: {
      artsCulture: "Shangri-La, the circus fields, the stone circle at dawn. Music is only half the story",
      musicDiscovery: "900 acts across 80 stages. You could come back ten years and never see the same thing",
      communityFeel: "A pilgrimage. People plan their year around this week",
      weatherResilience: "Mud is the admission fee. The crowd considers it part of the tradition",
    },
  },
  {
    slug: "green-man",
    name: "Green Man",
    region: "uk",
    locationLabel: "Brecon Beacons, Wales, UK",
    capacity: 25000,
    month: "August",
    emoji: "🌾",
    gradient: "linear-gradient(135deg, #2d5016 0%, #4a7c23 50%, #c8e6a0 100%)",
    genreTags: ["folk"],
    axes: {
      intimacy: 8,
      productionScale: 4,
      campingComfort: 5,
      musicDiscovery: 8,
      crowdEnergy: 3,
      artsCulture: 9,
      weatherResilience: 6,
      communityFeel: 9,
      durationCommitment: 5,
      genrePurity: 6,
    },
    pitchlines: {
      artsCulture: "Literature tent, science talks, comedy, and film alongside the music. The programme rewards curiosity",
      communityFeel: "Small enough that the same families come back every year. The Green Man settles it into ceremony",
      intimacy: "25,000 in the Brecon Beacons. You can hear the mountain stream between sets",
      musicDiscovery: "The lineup trusts you to discover rather than spoon-feeding headliners",
    },
  },
  {
    slug: "shambala",
    name: "Shambala",
    region: "uk",
    locationLabel: "Northamptonshire, UK",
    capacity: 15000,
    month: "August",
    emoji: "🦚",
    gradient: "linear-gradient(135deg, #4a0e4e 0%, #e8507b 40%, #ffd700 100%)",
    genreTags: ["electronic", "folk"],
    axes: {
      intimacy: 9,
      productionScale: 3,
      campingComfort: 4,
      musicDiscovery: 7,
      crowdEnergy: 6,
      artsCulture: 10,
      weatherResilience: 6,
      communityFeel: 10,
      durationCommitment: 5,
      genrePurity: 2,
    },
    pitchlines: {
      communityFeel: "No alcohol sold. Fancy dress mandatory. The weirdest, warmest crowd in the UK",
      artsCulture: "Sustainability workshops, costume parades, and art that makes you think. Music comes third",
      intimacy: "15,000 people in costume who all chose the same strange thing. Instant belonging",
      musicDiscovery: "World music, electronic, folk, spoken word. The programme defies categorisation",
    },
  },
  {
    slug: "wireless",
    name: "Wireless",
    region: "uk",
    locationLabel: "London, UK",
    capacity: 50000,
    month: "July",
    emoji: "🎤",
    gradient: "linear-gradient(135deg, #1a1a2e 0%, #e94560 50%, #f7a94b 100%)",
    genreTags: ["urban", "electronic"],
    axes: {
      intimacy: 3,
      productionScale: 8,
      campingComfort: 9,
      musicDiscovery: 3,
      crowdEnergy: 9,
      artsCulture: 2,
      weatherResilience: 3,
      communityFeel: 3,
      durationCommitment: 3,
      genrePurity: 7,
    },
    pitchlines: {
      crowdEnergy: "London\u2019s biggest weekend. The energy from the crowd matches any headline act",
      productionScale: "Arena-tier production in a park. Sound and visuals built for phone cameras",
      campingComfort: "No camping. Central London nightlife after the last act. Home by tube",
    },
  },
  {
    slug: "trnsmt",
    name: "TRNSMT",
    region: "uk",
    locationLabel: "Glasgow, UK",
    capacity: 50000,
    month: "July",
    emoji: "🎵",
    gradient: "linear-gradient(135deg, #1a1a2e 0%, #2563eb 50%, #22d3ee 100%)",
    genreTags: ["rock"],
    axes: {
      intimacy: 4,
      productionScale: 7,
      campingComfort: 9,
      musicDiscovery: 4,
      crowdEnergy: 8,
      artsCulture: 2,
      weatherResilience: 6,
      communityFeel: 6,
      durationCommitment: 4,
      genrePurity: 6,
    },
    pitchlines: {
      crowdEnergy: "Glasgow crowds sing louder than anywhere. The bands always say it",
      campingComfort: "No camping. Glasgow Green in the city centre. Pubs five minutes away",
      productionScale: "Scotland\u2019s biggest music weekend. Headline acts with city-skyline views",
    },
  },
  {
    slug: "end-of-the-road",
    name: "End of the Road",
    region: "uk",
    locationLabel: "Dorset, UK",
    capacity: 15000,
    month: "September",
    emoji: "🦌",
    gradient: "linear-gradient(135deg, #2c3e50 0%, #8b4513 40%, #daa520 100%)",
    genreTags: ["folk", "rock"],
    axes: {
      intimacy: 9,
      productionScale: 3,
      campingComfort: 5,
      musicDiscovery: 9,
      crowdEnergy: 3,
      artsCulture: 8,
      weatherResilience: 4,
      communityFeel: 9,
      durationCommitment: 4,
      genrePurity: 7,
    },
    pitchlines: {
      musicDiscovery: "The lineup that Pitchfork readers bookmark. Curation over clout",
      communityFeel: "15,000 people who came for the music, not the Instagram. Feels like a secret",
      intimacy: "Small enough that you\u2019ll share a conversation with the artist at the bar afterwards",
      artsCulture: "Woodland stages, a piano in the trees, and comedy that catches you off guard",
    },
  },

  // Europe (10 festivals)
  {
    slug: "tomorrowland",
    name: "Tomorrowland",
    region: "europe",
    locationLabel: "Boom, Belgium",
    capacity: 75000,
    month: "July",
    emoji: "🦋",
    gradient: "linear-gradient(135deg, #1a0533 0%, #4c1d95 40%, #f59e0b 100%)",
    genreTags: ["electronic"],
    axes: {
      intimacy: 2,
      productionScale: 10,
      campingComfort: 7,
      musicDiscovery: 5,
      crowdEnergy: 9,
      artsCulture: 7,
      weatherResilience: 5,
      communityFeel: 6,
      durationCommitment: 4,
      genrePurity: 8,
    },
    pitchlines: {
      productionScale: "The most expensive stage builds on the planet, twice a summer",
      artsCulture: "Every detail is themed. It\u2019s a theme park that happens to have DJs",
      crowdEnergy: "200 nationalities, one dance floor. The energy is indescribable",
      campingComfort: "DreamVille turns camping into a curated experience",
    },
  },
  {
    slug: "sziget",
    name: "Sziget",
    region: "europe",
    locationLabel: "Budapest, Hungary",
    capacity: 90000,
    month: "August",
    emoji: "🏝️",
    gradient: "linear-gradient(135deg, #1e3a5f 0%, #4ecdc4 50%, #ffe66d 100%)",
    genreTags: ["rock", "electronic"],
    axes: {
      intimacy: 3,
      productionScale: 7,
      campingComfort: 4,
      musicDiscovery: 7,
      crowdEnergy: 8,
      artsCulture: 8,
      weatherResilience: 3,
      communityFeel: 7,
      durationCommitment: 8,
      genrePurity: 2,
    },
    pitchlines: {
      durationCommitment: "A week on a Danube island. By day four it feels like home",
      artsCulture: "Theatre, circus, film, and talks fill the gaps between stages",
      musicDiscovery: "The lineup spans continents. You\u2019ll hear something from every corner of the world",
      communityFeel: "Island life creates bonds fast. You arrive solo, you leave with a crew",
    },
  },
  {
    slug: "primavera-sound",
    name: "Primavera Sound",
    region: "europe",
    locationLabel: "Barcelona, Spain",
    capacity: 100000,
    month: "June",
    emoji: "🌊",
    gradient: "linear-gradient(135deg, #0f2027 0%, #203a43 30%, #2c5364 60%, #f7971e 100%)",
    genreTags: ["rock", "electronic"],
    axes: {
      intimacy: 3,
      productionScale: 7,
      campingComfort: 9,
      musicDiscovery: 9,
      crowdEnergy: 6,
      artsCulture: 5,
      weatherResilience: 2,
      communityFeel: 5,
      durationCommitment: 4,
      genrePurity: 2,
    },
    pitchlines: {
      musicDiscovery: "The lineup that music journalists argue about for months beforehand",
      campingComfort: "No camping. Barcelona apartments, beach mornings, festival evenings",
      genrePurity: "Indie next to techno next to experimental. Proudly uncategorisable",
    },
  },
  {
    slug: "roskilde",
    name: "Roskilde",
    region: "europe",
    locationLabel: "Roskilde, Denmark",
    capacity: 130000,
    month: "July",
    emoji: "🍊",
    gradient: "linear-gradient(135deg, #f46b45 0%, #eea849 50%, #fceabb 100%)",
    genreTags: ["rock", "folk"],
    axes: {
      intimacy: 2,
      productionScale: 8,
      campingComfort: 3,
      musicDiscovery: 8,
      crowdEnergy: 8,
      artsCulture: 8,
      weatherResilience: 7,
      communityFeel: 9,
      durationCommitment: 9,
      genrePurity: 2,
    },
    pitchlines: {
      durationCommitment: "Eight days. The warm-up days before music starts are half the point",
      communityFeel: "Run by volunteers since 1971. The ethos is baked into the ground",
      artsCulture: "Art installations, activism, and naked runs before the first band plays",
      musicDiscovery: "The curation here is fearless. Headliners you\u2019d never expect",
    },
  },
  {
    slug: "rock-werchter",
    name: "Rock Werchter",
    region: "europe",
    locationLabel: "Werchter, Belgium",
    capacity: 88000,
    month: "July",
    emoji: "🎶",
    gradient: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #e94560 100%)",
    genreTags: ["rock"],
    axes: {
      intimacy: 3,
      productionScale: 8,
      campingComfort: 5,
      musicDiscovery: 5,
      crowdEnergy: 7,
      artsCulture: 3,
      weatherResilience: 6,
      communityFeel: 6,
      durationCommitment: 5,
      genrePurity: 5,
    },
    pitchlines: {
      productionScale: "Belgium\u2019s biggest rock weekend. Arena sound in a field",
      crowdEnergy: "The Belgian crowd knows every word. Singing louder than the PA",
      communityFeel: "A pilgrimage for Belgian rock fans since \u201974",
    },
  },
  {
    slug: "mysteryland",
    name: "Mysteryland",
    region: "europe",
    locationLabel: "Haarlemmermeer, Netherlands",
    capacity: 80000,
    month: "August",
    emoji: "🌀",
    gradient: "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
    genreTags: ["electronic"],
    axes: {
      intimacy: 4,
      productionScale: 8,
      campingComfort: 6,
      musicDiscovery: 6,
      crowdEnergy: 8,
      artsCulture: 6,
      weatherResilience: 4,
      communityFeel: 6,
      durationCommitment: 4,
      genrePurity: 7,
    },
    pitchlines: {
      productionScale: "Dutch sound system engineering at its finest",
      artsCulture: "The heritage garden stages blur art and music completely",
      crowdEnergy: "The Netherlands packs more dance energy per square metre than anywhere",
    },
  },
  {
    slug: "awakenings",
    name: "Awakenings",
    region: "europe",
    locationLabel: "Hilvarenbeek, Netherlands",
    capacity: 60000,
    month: "July",
    emoji: "🌲",
    gradient: "linear-gradient(135deg, #0a0a0a 0%, #1a472a 50%, #2d5a27 100%)",
    genreTags: ["electronic"],
    axes: {
      intimacy: 5,
      productionScale: 7,
      campingComfort: 4,
      musicDiscovery: 7,
      crowdEnergy: 8,
      artsCulture: 3,
      weatherResilience: 5,
      communityFeel: 7,
      durationCommitment: 4,
      genrePurity: 10,
    },
    pitchlines: {
      genrePurity: "Techno, nothing else. Every set, every stage, no exceptions",
      musicDiscovery: "The back-to-back sets here happen nowhere else on earth",
      communityFeel: "The techno faithful gather in Beekse Bergen forest every July",
      crowdEnergy: "Forest techno at 4am. The trees amplify everything",
    },
  },
  {
    slug: "hellfest",
    name: "Hellfest",
    region: "europe",
    locationLabel: "Clisson, France",
    capacity: 60000,
    month: "June",
    emoji: "🔥",
    gradient: "linear-gradient(135deg, #0d0d0d 0%, #4a0000 50%, #ff4500 100%)",
    genreTags: ["rock"],
    axes: {
      intimacy: 4,
      productionScale: 8,
      campingComfort: 3,
      musicDiscovery: 5,
      crowdEnergy: 9,
      artsCulture: 4,
      weatherResilience: 8,
      communityFeel: 9,
      durationCommitment: 5,
      genrePurity: 9,
    },
    pitchlines: {
      communityFeel: "The metal family reunion. Same crew, same field, every June",
      genrePurity: "Six stages of heavy music. From thrash to doom, nothing else gets in",
      crowdEnergy: "Loire Valley heat plus all-black denim. The mosh pits are spiritual",
      weatherResilience: "40 degrees in black. Everyone suffers together and loves it",
    },
  },
  {
    slug: "oya",
    name: "\u00d8ya",
    region: "europe",
    locationLabel: "Oslo, Norway",
    capacity: 60000,
    month: "August",
    emoji: "🌿",
    gradient: "linear-gradient(135deg, #1d4350 0%, #a43931 50%, #fceabb 100%)",
    genreTags: ["rock", "folk"],
    axes: {
      intimacy: 5,
      productionScale: 6,
      campingComfort: 8,
      musicDiscovery: 8,
      crowdEnergy: 5,
      artsCulture: 6,
      weatherResilience: 5,
      communityFeel: 7,
      durationCommitment: 5,
      genrePurity: 3,
    },
    pitchlines: {
      musicDiscovery: "Scandinavian curation with a knack for booking acts two years early",
      campingComfort: "Urban festival. Sleep in your Oslo Airbnb, walk to the park",
      communityFeel: "Oslo\u2019s music community shows up as one. Everyone knows someone",
      intimacy: "Big enough to be world-class, small enough to feel personal",
    },
  },
  {
    slug: "lowlands",
    name: "Lowlands",
    region: "europe",
    locationLabel: "Biddinghuizen, Netherlands",
    capacity: 60000,
    month: "August",
    emoji: "🎪",
    gradient: "linear-gradient(135deg, #2c3e50 0%, #3498db 50%, #e74c3c 100%)",
    genreTags: ["rock", "electronic"],
    axes: {
      intimacy: 5,
      productionScale: 7,
      campingComfort: 5,
      musicDiscovery: 8,
      crowdEnergy: 7,
      artsCulture: 8,
      weatherResilience: 7,
      communityFeel: 8,
      durationCommitment: 4,
      genrePurity: 2,
    },
    pitchlines: {
      artsCulture: "Film, science talks, comedy, and theatre alongside the music stages",
      musicDiscovery: "The Dutch Glastonbury. Curation that rewards wandering",
      communityFeel: "The most dedicated fanbase in the Netherlands. Sells out in hours",
      genrePurity: "Hip-hop tent next to a techno bunker next to indie. Deliberately eclectic",
    },
  },

  // North America (9 festivals)
  {
    slug: "coachella",
    name: "Coachella",
    region: "north-america",
    locationLabel: "Indio, California, USA",
    capacity: 125000,
    month: "April",
    emoji: "🌴",
    gradient: "linear-gradient(135deg, #ff9966 0%, #ff5e62 50%, #1a1a2e 100%)",
    genreTags: ["electronic", "rock"],
    axes: {
      intimacy: 1,
      productionScale: 10,
      campingComfort: 7,
      musicDiscovery: 6,
      crowdEnergy: 8,
      artsCulture: 8,
      weatherResilience: 2,
      communityFeel: 3,
      durationCommitment: 4,
      genrePurity: 1,
    },
    pitchlines: {
      productionScale: "Desert production on a scale that makes arena shows look small",
      artsCulture: "The art installations are as talked-about as the headliners",
      crowdEnergy: "125,000 people, two weekends, and the energy never dips",
      musicDiscovery: "The lineup defines what\u2019s next. Reunions, debuts, and surprise guests",
    },
  },
  {
    slug: "lollapalooza",
    name: "Lollapalooza Chicago",
    region: "north-america",
    locationLabel: "Chicago, USA",
    capacity: 100000,
    month: "August",
    emoji: "🌆",
    gradient: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    genreTags: ["rock", "urban"],
    axes: {
      intimacy: 2,
      productionScale: 9,
      campingComfort: 9,
      musicDiscovery: 5,
      crowdEnergy: 8,
      artsCulture: 4,
      weatherResilience: 5,
      communityFeel: 4,
      durationCommitment: 5,
      genrePurity: 2,
    },
    pitchlines: {
      productionScale: "Grant Park stages with the Chicago skyline as the backdrop",
      campingComfort: "No camping. City festival. Your hotel bed is a cab ride away",
      crowdEnergy: "Four days of Chicago summer energy in the heart of downtown",
    },
  },
  {
    slug: "bonnaroo",
    name: "Bonnaroo",
    region: "north-america",
    locationLabel: "Manchester, Tennessee, USA",
    capacity: 80000,
    month: "June",
    emoji: "\u2600\ufe0f",
    gradient: "linear-gradient(135deg, #f2994a 0%, #f2c94c 50%, #56ab2f 100%)",
    genreTags: ["rock", "electronic"],
    axes: {
      intimacy: 4,
      productionScale: 7,
      campingComfort: 3,
      musicDiscovery: 7,
      crowdEnergy: 8,
      artsCulture: 6,
      weatherResilience: 8,
      communityFeel: 9,
      durationCommitment: 5,
      genrePurity: 2,
    },
    pitchlines: {
      communityFeel: "Radiate Positivity is a slogan but the crowd actually lives it",
      weatherResilience: "Tennessee humidity is the test. Surviving together is the bond",
      musicDiscovery: "Superjams, surprise collaborations, and sets that only happen on this farm",
      crowdEnergy: "The What Stage crowd at midnight is something you feel in your spine",
    },
  },
  {
    slug: "edc-las-vegas",
    name: "EDC Las Vegas",
    region: "north-america",
    locationLabel: "Las Vegas, USA",
    capacity: 135000,
    month: "May",
    emoji: "🎰",
    gradient: "linear-gradient(135deg, #0f0c29 0%, #302b63 40%, #eb3349 100%)",
    genreTags: ["electronic"],
    axes: {
      intimacy: 1,
      productionScale: 10,
      campingComfort: 6,
      musicDiscovery: 4,
      crowdEnergy: 10,
      artsCulture: 5,
      weatherResilience: 3,
      communityFeel: 6,
      durationCommitment: 4,
      genrePurity: 9,
    },
    pitchlines: {
      productionScale: "The most expensive stage production in electronic music. Three nights of it",
      crowdEnergy: "135,000 people under the neon from dusk till dawn",
      genrePurity: "Every sub-genre of electronic music, dedicated stages for each",
      artsCulture: "The Speedway becomes a neon city. Carnival rides between sets",
    },
  },
  {
    slug: "burning-man",
    name: "Burning Man",
    region: "north-america",
    locationLabel: "Black Rock City, Nevada, USA",
    capacity: 80000,
    month: "September",
    emoji: "🔥",
    gradient: "linear-gradient(135deg, #c2703a 0%, #f7a94b 40%, #1a1a2e 100%)",
    genreTags: ["electronic"],
    axes: {
      intimacy: 5,
      productionScale: 6,
      campingComfort: 1,
      musicDiscovery: 9,
      crowdEnergy: 7,
      artsCulture: 10,
      weatherResilience: 10,
      communityFeel: 10,
      durationCommitment: 10,
      genrePurity: 2,
    },
    pitchlines: {
      artsCulture: "Not a festival. A temporary city built on radical self-expression",
      communityFeel: "Gifting economy, no commerce, no spectators. Everyone participates",
      durationCommitment: "A week in the desert changes how you think about everything else",
      weatherResilience: "Dust storms, 40-degree swings, zero infrastructure. You bring everything",
    },
  },
  {
    slug: "governors-ball",
    name: "Governors Ball",
    region: "north-america",
    locationLabel: "New York, USA",
    capacity: 50000,
    month: "June",
    emoji: "🗽",
    gradient: "linear-gradient(135deg, #2c3e50 0%, #4ca1af 50%, #c9d6ff 100%)",
    genreTags: ["urban", "rock"],
    axes: {
      intimacy: 5,
      productionScale: 7,
      campingComfort: 9,
      musicDiscovery: 5,
      crowdEnergy: 7,
      artsCulture: 4,
      weatherResilience: 4,
      communityFeel: 4,
      durationCommitment: 3,
      genrePurity: 2,
    },
    pitchlines: {
      campingComfort: "No camping. It\u2019s New York. Pizza at midnight, subway home",
      crowdEnergy: "NYC energy compressed into Flushing Meadows for a weekend",
      musicDiscovery: "The lineup that shows what New York is listening to right now",
    },
  },
  {
    slug: "osheaga",
    name: "Osheaga",
    region: "north-america",
    locationLabel: "Montreal, Canada",
    capacity: 45000,
    month: "August",
    emoji: "🍁",
    gradient: "linear-gradient(135deg, #355c7d 0%, #6c5b7b 50%, #c06c84 100%)",
    genreTags: ["rock", "electronic"],
    axes: {
      intimacy: 5,
      productionScale: 7,
      campingComfort: 8,
      musicDiscovery: 7,
      crowdEnergy: 6,
      artsCulture: 5,
      weatherResilience: 5,
      communityFeel: 6,
      durationCommitment: 4,
      genrePurity: 3,
    },
    pitchlines: {
      musicDiscovery: "French-Canadian curation that balances big names with genuine discoveries",
      campingComfort: "Island festival, city comforts. Montreal\u2019s best restaurants are twenty minutes away",
      intimacy: "Mid-sized and curated. Big enough to impress, small enough to navigate",
    },
  },
  {
    slug: "electric-forest",
    name: "Electric Forest",
    region: "north-america",
    locationLabel: "Rothbury, Michigan, USA",
    capacity: 45000,
    month: "June",
    emoji: "\u2728",
    gradient: "linear-gradient(135deg, #0b3d0b 0%, #1a472a 40%, #7b4397 70%, #dc2430 100%)",
    genreTags: ["electronic"],
    axes: {
      intimacy: 6,
      productionScale: 7,
      campingComfort: 4,
      musicDiscovery: 8,
      crowdEnergy: 7,
      artsCulture: 9,
      weatherResilience: 6,
      communityFeel: 10,
      durationCommitment: 5,
      genrePurity: 5,
    },
    pitchlines: {
      communityFeel: "The loyalty programme exists because everyone comes back. It\u2019s family",
      artsCulture: "Sherwood Forest lit up at night is a world that doesn\u2019t exist anywhere else",
      musicDiscovery: "The forest stages surface acts you\u2019ll be telling people about for years",
      intimacy: "45,000 in a forest feels intimate. The trees create natural rooms",
    },
  },

  // ANZ (4 festivals)
  {
    slug: "laneway",
    name: "Laneway Festival",
    region: "anz",
    locationLabel: "Sydney, Australia",
    capacity: 25000,
    month: "February",
    emoji: "🦘",
    gradient: "linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)",
    genreTags: ["rock", "folk"],
    axes: {
      intimacy: 7,
      productionScale: 5,
      campingComfort: 9,
      musicDiscovery: 9,
      crowdEnergy: 5,
      artsCulture: 4,
      weatherResilience: 3,
      communityFeel: 6,
      durationCommitment: 1,
      genrePurity: 3,
    },
    pitchlines: {
      musicDiscovery: "The lineup that Australian indie kids argue about for months",
      intimacy: "25,000 and one day. Every set feels close and personal",
      campingComfort: "One-day city festival. Brunch before, pubs after",
    },
  },
  {
    slug: "splendour",
    name: "Splendour in the Grass",
    region: "anz",
    locationLabel: "Byron Bay, Australia",
    capacity: 50000,
    month: "July",
    emoji: "🌈",
    gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 50%, #f7971e 100%)",
    genreTags: ["rock", "electronic"],
    axes: {
      intimacy: 5,
      productionScale: 7,
      campingComfort: 4,
      musicDiscovery: 6,
      crowdEnergy: 7,
      artsCulture: 5,
      weatherResilience: 7,
      communityFeel: 7,
      durationCommitment: 4,
      genrePurity: 3,
    },
    pitchlines: {
      communityFeel: "Byron Bay\u2019s annual gathering. The crowd treats it like a reunion",
      weatherResilience: "2022 taught everyone to pack for mud. The crowd wears it proudly",
      crowdEnergy: "Australian festival energy in the hinterland. Loose and loud",
    },
  },
  {
    slug: "beyond-the-valley",
    name: "Beyond The Valley",
    region: "anz",
    locationLabel: "Victoria, Australia",
    capacity: 18000,
    month: "December",
    emoji: "🎆",
    gradient: "linear-gradient(135deg, #141e30 0%, #243b55 50%, #f7971e 100%)",
    genreTags: ["electronic"],
    axes: {
      intimacy: 8,
      productionScale: 6,
      campingComfort: 5,
      musicDiscovery: 6,
      crowdEnergy: 8,
      artsCulture: 4,
      weatherResilience: 4,
      communityFeel: 7,
      durationCommitment: 5,
      genrePurity: 7,
    },
    pitchlines: {
      intimacy: "18,000 people ringing in the new year together. Small enough to feel it",
      crowdEnergy: "NYE countdown surrounded by people who chose music over fireworks",
      communityFeel: "The crew that spends every new year in the same Victorian valley",
    },
  },
  {
    slug: "rhythm-vines",
    name: "Rhythm and Vines",
    region: "anz",
    locationLabel: "Gisborne, New Zealand",
    capacity: 25000,
    month: "December",
    emoji: "🍇",
    gradient: "linear-gradient(135deg, #1d976c 0%, #93f9b9 50%, #f7971e 100%)",
    genreTags: ["electronic"],
    axes: {
      intimacy: 7,
      productionScale: 5,
      campingComfort: 5,
      musicDiscovery: 5,
      crowdEnergy: 7,
      artsCulture: 3,
      weatherResilience: 5,
      communityFeel: 7,
      durationCommitment: 4,
      genrePurity: 7,
    },
    pitchlines: {
      communityFeel: "First sunrise of the new year, shared with 25,000 others on a vineyard",
      intimacy: "Waiohika Estate is small enough that you\u2019ll find your people by day two",
      crowdEnergy: "New Year\u2019s Eve on a vineyard at the edge of the world",
    },
  },
];

// Registry membership owns the available destinations. Preserve richer existing
// editorial profiles while ensuring every new destination can be recommended.
export const FESTIVAL_POOL: FestivalQuizData[] = FESTIVALS.map((festival) => {
  const curated = CURATED_FESTIVAL_POOL.find((profile) => profile.slug === festival.slug);
  return curated ? { ...curated, name: festival.name, locationLabel: `${festival.city}, ${festival.country}`, month: catalogueQuizProfile(festival).month } : catalogueQuizProfile(festival);
});
